import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const WarpingPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en/');

  const TEXT_JA = {
    breadcrumb: { home: 'ホーム', troubleshooting: 'トラブル対策', title: '反り（ワーピング）' },
    badge: '重要',
    title: '反り（ワーピング）',
    description: 'プリント物の端が浮き上がる現象。特に大型造形や角部分で発生しやすい。',
    symptoms: {
      title: '症状の例',
      items: [
        '造形物の四隅がベッドから浮き上がる',
        '造形途中でベッドから剥がれてしまう',
        '底面が反り返って平面にならない',
        '大型造形物の端部分が持ち上がる'
      ]
    },
    causes: {
      title: '主な原因',
      items: [
        { title: '1. ベッド温度不足', desc: 'ベッド温度が低いと、樹脂の接着力が不足し、冷却時の収縮で反りが発生します。特にABSやASAなど収縮率の高いフィラメントで顕著です。' },
        { title: '2. 冷却速度過多', desc: 'パートクーリングファンが強すぎると、上層と下層の温度差が大きくなり、内部応力により反りが発生します。' },
        { title: '3. 接着不良', desc: 'ベッド表面の汚れや、ファーストレイヤーの設定不良により、初期接着が不十分な場合に反りが発生しやすくなります。' },
        { title: '4. 環境温度の影響', desc: '室温が低い、エアコンの風が直接当たるなど、環境要因により急激な冷却が起こると反りが発生します。' }
      ]
    },
    solutions: {
      title: '対策・チェックポイント',
      items: [
        {
          title: 'ベッド温度の上昇',
          desc: 'フィラメントに応じて適切なベッド温度に設定します。',
          points: ['PLA: 60-70°C（通常は60°Cで十分）', 'PETG: 70-80°C', 'ABS/ASA: 90-110°C（エンクロージャー推奨）', 'PC: 100-120°C']
        },
        {
          title: 'ファン速度の調整',
          desc: '初期レイヤーではファンを弱めるか停止し、徐々に上げていきます。',
          points: ['最初の3-5層はファン0-20%', 'その後徐々に目標速度まで上昇', 'ABS/ASAは全体的にファン弱め（0-30%）']
        },
        {
          title: 'ブリム・ラフトの使用',
          desc: '接地面積を増やすことで反りを抑制します。',
          points: ['ブリム: 5-10mm幅で設定（小型〜中型造形）', 'ラフト: 大型造形や反りやすい材料で有効', 'スカート: 反り対策には効果薄い']
        },
        {
          title: 'ベッド表面の清掃',
          desc: '定期的な清掃で接着力を維持します。',
          points: ['IPAやエタノールで油分を除去', '必要に応じて接着剤（スティックのり等）を使用', 'PEIシートの場合は軽く研磨も有効']
        },
        {
          title: 'エンクロージャーの活用',
          desc: 'ABS/ASAなど収縮率の高い材料では特に有効です。',
          points: ['庫内温度を一定に保つ', '外気の影響を遮断', '簡易的にはダンボール等でも効果あり']
        }
      ]
    },
    related: {
      title: '関連情報',
      items: [
        { title: 'ABS フィラメント', desc: '反りが発生しやすい材料の代表例', link: '/filament/abs' },
        { title: 'ASA フィラメント', desc: 'ABS同様に反り対策が必要', link: '/filament/asa' }
      ]
    },
    backLink: 'トラブル対策一覧に戻る'
  };

  const TEXT_EN = {
    breadcrumb: { home: 'Home', troubleshooting: 'Troubleshooting', title: 'Warping' },
    badge: 'Important',
    title: 'Warping',
    description: 'Edges of printed parts lifting from the bed. Especially common in large prints and corners.',
    symptoms: {
      title: 'Symptoms',
      items: [
        'Corners of print lifting from bed',
        'Print detaching from bed during printing',
        'Bottom surface curling up, not flat',
        'Edges of large prints lifting'
      ]
    },
    causes: {
      title: 'Main Causes',
      items: [
        { title: '1. Insufficient Bed Temperature', desc: 'Low bed temperature reduces adhesion, causing warping due to shrinkage during cooling. Especially noticeable with high-shrinkage filaments like ABS and ASA.' },
        { title: '2. Excessive Cooling', desc: 'Too much part cooling creates temperature differences between layers, causing internal stress and warping.' },
        { title: '3. Poor Adhesion', desc: 'Dirty bed surface or improper first layer settings result in insufficient initial adhesion, making warping more likely.' },
        { title: '4. Environmental Temperature', desc: 'Low room temperature or direct airflow from air conditioning causes rapid cooling and warping.' }
      ]
    },
    solutions: {
      title: 'Solutions & Checkpoints',
      items: [
        {
          title: 'Increase Bed Temperature',
          desc: 'Set appropriate bed temperature for the filament.',
          points: ['PLA: 60-70°C (usually 60°C is sufficient)', 'PETG: 70-80°C', 'ABS/ASA: 90-110°C (enclosure recommended)', 'PC: 100-120°C']
        },
        {
          title: 'Adjust Fan Speed',
          desc: 'Keep fan low or off for initial layers, gradually increase.',
          points: ['First 3-5 layers: fan 0-20%', 'Gradually increase to target speed', 'ABS/ASA: keep fan low overall (0-30%)']
        },
        {
          title: 'Use Brim or Raft',
          desc: 'Increase contact area to prevent warping.',
          points: ['Brim: 5-10mm width (small to medium prints)', 'Raft: effective for large prints or warp-prone materials', 'Skirt: not effective for warping']
        },
        {
          title: 'Clean Bed Surface',
          desc: 'Regular cleaning maintains adhesion.',
          points: ['Remove oils with IPA or ethanol', 'Use adhesive (glue stick, etc.) if needed', 'Light sanding effective for PEI sheets']
        },
        {
          title: 'Use Enclosure',
          desc: 'Especially effective for high-shrinkage materials like ABS/ASA.',
          points: ['Maintain constant chamber temperature', 'Block external air influence', 'Simple cardboard enclosure also effective']
        }
      ]
    },
    related: {
      title: 'Related Information',
      items: [
        { title: 'ABS Filament', desc: 'Representative material prone to warping', link: '/filament/abs' },
        { title: 'ASA Filament', desc: 'Requires warping prevention like ABS', link: '/filament/asa' }
      ]
    },
    backLink: 'Back to Troubleshooting List'
  };

  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;
  const prefix = isEnglish ? '/en' : '';

  const breadcrumbItems = [
    { label: TEXT.breadcrumb.home, path: `${prefix}/` },
    { label: TEXT.breadcrumb.troubleshooting, path: `${prefix}/troubleshooting` },
    { label: TEXT.breadcrumb.title }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <span className="tech-label bg-[#b56b3c] text-white">{TEXT.badge}</span>
                <h1 className="text-4xl font-bold text-white font-['Exo']">
                  {TEXT.title}
                </h1>
              </div>
              <p className="text-metal-light text-lg">
                {TEXT.description}
              </p>
              <div className="section-divider"></div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.symptoms.title}
              </h2>
              <ul className="space-y-3 text-metal-light">
                {TEXT.symptoms.items.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <i className="ri-arrow-right-s-line text-accent mt-1"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.causes.title}
              </h2>
              <div className="space-y-4">
                {TEXT.causes.items.map((item, idx) => (
                  <div key={idx} className="border-l-4 border-l-[#b56b3c] pl-4">
                    <h3 className="text-lg font-semibold text-accent font-['Exo'] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.solutions.title}
              </h2>
              <div className="space-y-4">
                {TEXT.solutions.items.map((item, idx) => (
                  <div key={idx} className="clickable-card p-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-[#5f8d63] text-xl mt-1"></i>
                      <div>
                        <h3 className="text-lg font-semibold text-white font-['Exo'] mb-2">
                          {item.title}
                        </h3>
                        <p className="text-metal-light text-sm mb-2">
                          {item.desc}
                        </p>
                        <ul className="text-metal-light text-sm space-y-1 ml-4">
                          {item.points.map((point, pidx) => (
                            <li key={pidx}>• {point}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.related.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {TEXT.related.items.map((item, idx) => (
                  <Link
                    key={idx}
                    to={`${prefix}${item.link}`}
                    className="clickable-card p-4 hover:border-l-4 hover:border-l-accent transition-all"
                  >
                    <h3 className="text-white font-['Exo'] font-semibold mb-1">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <Link
                to={`${prefix}/troubleshooting`}
                className="flex items-center space-x-2 text-accent hover:text-white transition-colors font-['Roboto_Mono']"
              >
                <i className="ri-arrow-left-line"></i>
                <span>{TEXT.backLink}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default WarpingPage;
